import React, { useState } from "react";

import { AccountBubbleDriftChevron } from "./AccountBubbleDriftChevron";
import { resolveAccountBubbleRole } from "./portfolioBubbleRole";
import PortfolioCellAccountPickerModal from "./PortfolioCellAccountPickerModal";
import {
  formatAggregateRl,
  formatLevel,
  getPrototypeAlBubbleClass,
  getInitials,
  isUnassessedLevelLabel,
  UNASSESSED_LEVEL_TITLE,
} from "../account-relationships/relationshipUtils";

export const PORTFOLIO_CLUSTER_VISIBLE = 3;

function AccountBubble({
  account,
  onOpen = undefined,
  size = 52,
  style,
  asButton = true,
  searchActive = false,
  isSearchMatch = false,
  forceExecutiveRing = false,
}) {
  const alLabel =
    account.alLevel === null || account.alLevel === undefined
      ? "AL ?"
      : formatLevel("AL", account.alLevel);
  const rlLabel =
    account.rlAggregate === null || account.rlAggregate === undefined
      ? "RL ?"
      : formatAggregateRl(account.rlAggregate);
  const { strokeClass: roleStrokeClass, label: roleLabel } =
    resolveAccountBubbleRole(account, { forceExecutiveRing });
  const className = [
    "acct-bubble",
    getPrototypeAlBubbleClass(account.alLevel),
    roleStrokeClass,
    searchActive && isSearchMatch && "acct-bubble--search-match",
    searchActive && !isSearchMatch && "acct-bubble--search-dim",
  ]
    .filter(Boolean)
    .join(" ");
  const bubbleStyle = { width: size, height: size, flexShrink: 0, ...style };
  const title = [
    account.name,
    `${alLabel} / ${rlLabel}`,
    isUnassessedLevelLabel(alLabel) || isUnassessedLevelLabel(rlLabel)
      ? UNASSESSED_LEVEL_TITLE
      : null,
    roleLabel ? `Your role: ${roleLabel}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const content = (
    <>
      <span
        className="ab-init"
        style={size < 40 ? { fontSize: 11 } : undefined}
      >
        {getInitials(account.name)}
      </span>
      {size >= 40 ? <span className="ab-name">{account.name}</span> : null}
      <AccountBubbleDriftChevron rlAggregate={account.rlAggregate} />
    </>
  );

  if (!asButton) {
    return (
      <span className={className} style={bubbleStyle} title={title} aria-hidden>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={className}
      style={bubbleStyle}
      title={title}
      onClick={event => {
        event.stopPropagation();
        onOpen(account);
      }}
    >
      {content}
    </button>
  );
}

export function PortfolioGridCellCluster({
  accounts,
  alRow,
  rlColumn,
  onOpenAccount,
  searchActive = false,
  matchingIds = null,
  searchQuery = "",
  forceExecutiveRing = false,
  /** Single (non-clustered) bubble size — default 52 matches this shared
   * component's original look (Relationships page). My Accounts passes 42
   * to match my-accounts-portfolio-v4.html's `.acct-bubble` (mock line
   * 1558). Does not affect the 3-stack cluster-preview bubbles (still a
   * fixed, separate 36px — that overlap-preview size has no mock
   * equivalent to match, so it's left as this app's own existing design). */
  bubbleSize = 52,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!accounts.length) return null;

  const isMatch = account => Boolean(matchingIds?.has(String(account.id)));

  const useCluster = accounts.length >= PORTFOLIO_CLUSTER_VISIBLE;
  const visible = useCluster
    ? accounts.slice(0, PORTFOLIO_CLUSTER_VISIBLE)
    : accounts;
  const overflowCount = Math.max(
    0,
    accounts.length - PORTFOLIO_CLUSTER_VISIBLE
  );

  if (!useCluster) {
    return (
      <>
        {visible.map((account, index) => (
          <AccountBubble
            key={`${account.id}-${index}`}
            account={account}
            onOpen={onOpenAccount}
            size={bubbleSize}
            searchActive={searchActive}
            isSearchMatch={isMatch(account)}
            forceExecutiveRing={forceExecutiveRing}
          />
        ))}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="pg-cluster-trigger"
        aria-label={`${accounts.length} accounts in this cell. Open list to select.`}
        title={`${accounts.length} accounts — click to choose`}
        onClick={() => setPickerOpen(true)}
      >
        <span className="pg-cluster-stack" aria-hidden>
          {visible.map(account => (
            <AccountBubble
              key={account.id}
              account={account}
              size={36}
              asButton={false}
              searchActive={searchActive}
              isSearchMatch={isMatch(account)}
              forceExecutiveRing={forceExecutiveRing}
            />
          ))}
        </span>
        {overflowCount > 0 ? (
          <span className="pg-cluster-more" aria-hidden>
            +{overflowCount}
          </span>
        ) : null}
      </button>

      <PortfolioCellAccountPickerModal
        open={pickerOpen}
        accounts={accounts}
        alRow={alRow}
        rlColumn={rlColumn}
        onClose={() => setPickerOpen(false)}
        onSelectAccount={onOpenAccount}
        gridSearchQuery={searchQuery}
        forceExecutiveRing={forceExecutiveRing}
      />
    </>
  );
}

export default PortfolioGridCellCluster;
